import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { getAuthToken } from "./api";
import "./InstitutionalDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// ─── Report helpers ────────────────────────────────────────────────────────────

function escHtml(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function fmtMs(ms) {
  if (!ms) return "—";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

function generateReportHTML(report, campaign) {
  const mcq = report.mcq;
  const coding = report.coding;
  const p = report.proctoring || {};
  const answers = mcq?.answers || [];
  const qCache = mcq?.questions_cache || [];

  const scoreCard = (val, lbl) =>
    `<div class="sc"><div class="sc-val">${val ?? "—"}</div><div class="sc-lbl">${lbl}</div></div>`;

  const mcqSection = mcq ? `
    <h2>MCQ Results</h2>
    <div class="score-row">
      ${scoreCard((mcq.scores?.total ?? "—") + "%", "Overall")}
      ${scoreCard((mcq.scores?.technical ?? "—") + "%", "Technical")}
      ${scoreCard((mcq.scores?.aptitude ?? "—") + "%", "Aptitude")}
      ${scoreCard((mcq.scores?.workstyle ?? "—") + "%", "Workstyle")}
      ${scoreCard(mcq.correct_answers + " / " + mcq.total_questions, "Correct")}
      ${scoreCard(fmtMs(mcq.time_used_ms), "Time")}
    </div>` : "";

  const codingSection = coding ? `
    <h2>Coding Results</h2>
    ${(coding.submissions || []).map((sub, i) => `
      <div class="coding-block">
        <div class="coding-head">
          <strong>Problem ${i + 1}${sub.title ? ": " + escHtml(sub.title) : ""}</strong>
          <span class="${sub.status === "accepted" ? "acc" : "rej"}">${escHtml(sub.status || "not submitted")}</span>
        </div>
        ${sub.description ? `<div class="prob-desc"><strong>Problem Statement:</strong><br>${escHtml(sub.description)}</div>` : ""}
        ${sub.code ? `<div class="code-label">Submitted Code${sub.language ? " (" + escHtml(sub.language) + ")" : ""}:</div><pre>${escHtml(sub.code)}</pre>` : '<p class="no-code">No code submitted</p>'}
      </div>`).join("")}` : "";

  const score = p.integrity_score ?? 100;
  const level = (p.integrity_level || "high").toLowerCase();
  const levelColor = level === "high" ? "#155724" : level === "medium" ? "#856404" : "#721c24";
  const levelBg   = level === "high" ? "#d4edda"  : level === "medium" ? "#fff3cd"  : "#f8d7da";

  const flagRow = (label, val, bad = false) => {
    const isFlag = bad && val > 0;
    return `<div class="ig-row">
      <span class="ig-label">${label}</span>
      <span class="ig-val${isFlag ? " ig-flag" : ""}">${val}</span>
    </div>`;
  };

  const totalGazeAway = (p.gaze_look_left ?? 0) + (p.gaze_look_right ?? 0);
  const cameraUsed = totalGazeAway > 0 || (p.gaze_look_down ?? 0) > 0 ||
                     (p.face_not_visible ?? 0) > 0 || (p.multiple_faces ?? 0) > 0;

  const cameraSection = cameraUsed ? `
    ${flagRow("Gaze Away — Left", p.gaze_look_left ?? 0, true)}
    ${flagRow("Gaze Away — Right", p.gaze_look_right ?? 0, true)}
    ${flagRow("Looked Down", p.gaze_look_down ?? 0, true)}
    ${flagRow("Face Not Visible (×4 s)", p.face_not_visible ?? 0, true)}
    <div class="ig-row">
      <span class="ig-label">Multiple Faces Detected</span>
      <span class="ig-val${(p.multiple_faces ?? 0) > 0 ? " ig-flag" : ""}">${(p.multiple_faces ?? 0) > 0 ? "Yes" : "No"}</span>
    </div>` :
    `<div class="ig-na">Camera monitoring was not active for this session.</div>`;

  const timestamps = p.timestamps || [];
  const eventTypeLabel = { tab_hidden: "Tab Hidden", window_blur: "Window Blur", copy: "Copy", paste: "Paste", cut: "Cut", face_not_visible: "No Face", multiple_faces: "Multiple Faces" };
  const evtLogRows = timestamps.slice(-30).map(t => {
    const ts = t.at ? new Date(t.at).toLocaleTimeString() : "";
    const label = eventTypeLabel[t.type] || t.type;
    const isBad = ["tab_hidden","copy","paste","cut","multiple_faces"].includes(t.type);
    return `<div class="evt-row${isBad ? " evt-warn" : ""}"><span class="evt-time">${ts}</span><span class="evt-type">${escHtml(label)}</span></div>`;
  }).join("");

  const integritySection = `
    <h2>Integrity Analysis</h2>
    <div class="integrity-header">
      <div class="integrity-badge" style="background:${levelBg};color:${levelColor}">
        <div class="ibadge-score">${score}</div>
        <div class="ibadge-lvl">${level.toUpperCase()}</div>
      </div>
      <div class="integrity-meta">
        <strong>Integrity Score: ${score}/100</strong>
        <span>Camera: ${cameraUsed ? "Active" : "Not enabled"}</span>
      </div>
    </div>
    <div class="integrity-groups">
      <div class="ig">
        <div class="ig-title">Browser Behavior</div>
        ${flagRow("Tab Switches", p.tab_switch_count ?? 0, true)}
        ${flagRow("Window Focus Lost", p.window_blur_count ?? 0, true)}
        ${flagRow("Copy / Paste Attempts", p.copy_paste_attempts ?? 0, true)}
      </div>
      <div class="ig">
        <div class="ig-title">Camera Monitoring</div>
        ${cameraSection}
      </div>
    </div>
    ${timestamps.length > 0 ? `
    <div class="evt-log">
      <div class="evt-log-title">Event Log (${timestamps.length} events, last 30 shown)</div>
      ${evtLogRows}
    </div>` : ""}
  `;

  const renderQBlock = (q, i, selected, correct) => {
    const isCorrect = selected && selected === correct;
    const wasSkipped = !selected;
    return `<div class="q-block">
      <div class="q-meta">Q${i + 1} · ${escHtml(q.section || "")}${q.topic ? " · " + escHtml(q.topic) : ""}
        <span class="${isCorrect ? "q-ok" : wasSkipped ? "q-skip" : "q-err"}">${isCorrect ? "✓ Correct" : wasSkipped ? "Skipped / Not recorded" : "✗ Wrong"}</span>
      </div>
      <div class="q-text">${escHtml(q.text)}</div>
      ${q.options ? Object.entries(q.options).map(([k, v]) => {
        let cls = "opt";
        if (k === correct) cls += " opt-correct";
        if (selected && k === selected && k !== correct) cls += " opt-wrong";
        return `<div class="${cls}"><strong>${k}.</strong> ${escHtml(String(v))}${k === correct ? " ✓" : ""}${selected && k === selected ? " ← candidate's answer" : ""}</div>`;
      }).join("") : ""}
    </div>`;
  };

  let answerSection = "";
  if (answers.length > 0) {
    answerSection = `
    <h2>MCQ Answer Review (${answers.length} questions)</h2>
    ${answers.map((a, i) => renderQBlock(a, i, a.selected, a.correct)).join("")}`;
  } else if (qCache.length > 0) {
    answerSection = `
    <h2>MCQ Questions (${qCache.length} questions)</h2>
    <p class="note" style="margin-bottom:14px">This candidate's individual answer selections were not recorded (older submission). Correct answers are highlighted.</p>
    ${qCache.map((q, i) => renderQBlock(q, i, null, q.correct)).join("")}`;
  } else if (mcq) {
    answerSection = `<p class="note">Detailed answer breakdown not available for this attempt.</p>`;
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Candidate Report — ${escHtml(report.name)}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; color: #222; padding: 32px; max-width: 960px; margin: 0 auto; }
  .print-btn { margin-bottom: 20px; }
  .print-btn button { padding: 8px 20px; background: #4a4a9e; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 0.9rem; }
  h1 { font-size: 1.5rem; margin-bottom: 4px; }
  .meta { color: #666; font-size: 0.88rem; margin-bottom: 24px; }
  h2 { font-size: 1rem; border-bottom: 2px solid #eee; padding-bottom: 6px; margin: 28px 0 14px; color: #333; text-transform: uppercase; letter-spacing: 0.04em; }
  .score-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 8px; }
  .sc { border: 1px solid #ddd; border-radius: 8px; padding: 12px 16px; text-align: center; min-width: 110px; }
  .sc-val { font-size: 1.3rem; font-weight: 700; color: #222; }
  .sc-lbl { font-size: 0.72rem; color: #888; text-transform: uppercase; margin-top: 2px; }
  .coding-block { border: 1px solid #ddd; border-radius: 8px; overflow: hidden; margin-bottom: 16px; }
  .coding-head { background: #f8f9fa; padding: 10px 16px; border-bottom: 1px solid #ddd; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
  .prob-desc { padding: 12px 16px; border-bottom: 1px solid #eee; background: #fafbfc; font-size: 0.84rem; color: #333; line-height: 1.65; white-space: pre-wrap; }
  .code-label { padding: 8px 16px 4px; font-size: 0.75rem; color: #888; background: #f5f5f5; border-bottom: 1px solid #eee; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
  .acc { color: green; font-weight: 700; }
  .rej { color: #c00; font-weight: 700; }
  pre { background: #f5f5f5; padding: 14px 16px; font-size: 0.8rem; white-space: pre-wrap; font-family: monospace; line-height: 1.5; overflow-x: auto; }
  .no-code { padding: 12px 16px; color: #888; font-style: italic; font-size: 0.85rem; }
  .q-block { border: 1px solid #eee; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px; }
  .q-meta { font-size: 0.78rem; color: #888; margin-bottom: 6px; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .q-ok { color: green; font-weight: 700; margin-left: auto; }
  .q-err { color: #c00; font-weight: 700; margin-left: auto; }
  .q-skip { color: #888; margin-left: auto; }
  .q-text { font-weight: 500; font-size: 0.9rem; margin-bottom: 10px; }
  .opt { padding: 5px 10px; border-radius: 4px; font-size: 0.85rem; margin: 3px 0; background: #f8f9fa; color: #444; }
  .opt-correct { background: #d4edda; color: #155724; }
  .opt-wrong { background: #f8d7da; color: #721c24; }
  .note { color: #888; font-style: italic; font-size: 0.88rem; }
  .integrity-header { display: flex; align-items: center; gap: 20px; margin-bottom: 18px; flex-wrap: wrap; }
  .integrity-badge { border-radius: 10px; padding: 12px 20px; text-align: center; min-width: 90px; }
  .ibadge-score { font-size: 1.8rem; font-weight: 800; line-height: 1; }
  .ibadge-lvl { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; margin-top: 2px; }
  .integrity-meta { display: flex; flex-direction: column; gap: 4px; font-size: 0.88rem; color: #444; }
  .integrity-groups { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
  @media (max-width: 600px) { .integrity-groups { grid-template-columns: 1fr; } }
  .ig { border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
  .ig-title { background: #f0f0f0; padding: 8px 14px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #555; }
  .ig-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 14px; border-top: 1px solid #f0f0f0; font-size: 0.85rem; }
  .ig-label { color: #444; }
  .ig-val { font-weight: 700; color: #333; }
  .ig-flag { color: #c00; }
  .ig-na { padding: 12px 14px; font-size: 0.83rem; color: #888; font-style: italic; border-top: 1px solid #f0f0f0; }
  .evt-log { border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
  .evt-log-title { background: #f0f0f0; padding: 8px 14px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #555; }
  .evt-row { display: flex; gap: 12px; padding: 5px 14px; border-top: 1px solid #f5f5f5; font-size: 0.8rem; }
  .evt-time { color: #888; min-width: 70px; }
  .evt-type { color: #444; }
  .evt-warn .evt-type { color: #c00; font-weight: 600; }
  @media print { .print-btn { display: none !important; } body { padding: 16px; } }
</style>
</head><body>
<div class="print-btn"><button onclick="window.print()">Print / Save as PDF</button></div>
<h1>${escHtml(report.name)}</h1>
<div class="meta">${escHtml(report.email)} &nbsp;·&nbsp; ${escHtml(campaign.title)}</div>
${mcqSection}${codingSection}${answerSection}${integritySection}
</body></html>`;
}

function downloadCSV(results, campaign) {
  const hasCoding = !!(campaign?.config?.include_coding) ||
    results.some(r => r.coding_solved != null);
  const isInterview = campaign?.event_type === "INTERVIEW";
  const isCoding = campaign?.event_type === "CODING";

  let headers;
  if (isInterview) {
    headers = ["Name", "Email", "Status", "Overall", "Technical", "Communication", "Integrity Score", "Integrity Level", "Started At"];
  } else if (isCoding) {
    headers = ["Name", "Email", "Solved", "Total Problems", "Integrity Score", "Integrity Level", "Tab Switches", "Window Blurs", "Copy-Paste", "Date"];
  } else {
    headers = ["Name", "Email", "Role", "MCQ Total%", "Technical%", "Aptitude%", "Workstyle%", "Correct", "Total Questions"];
    if (hasCoding) headers.push("Coding Solved", "Coding Total");
    headers.push("Integrity Score", "Integrity Level", "Tab Switches", "Window Blurs", "Copy-Paste", "Date");
  }

  const rows = results.map(r => {
    if (isInterview) {
      return [r.name, r.email, r.status, r.overall_score ?? "", r.technical_score ?? "",
        r.communication_score ?? "", r.integrity_score ?? "", r.integrity_level || "", r.started_at || ""];
    }
    if (isCoding) {
      return [r.name, r.email, r.solved ?? "", r.total ?? "",
        r.integrity_score ?? "", r.integrity_level || "",
        r.tab_switch_count ?? "", r.window_blur_count ?? "", r.copy_paste_attempts ?? "",
        r.created_at || ""];
    }
    const row = [r.name, r.email, r.role || "",
      r.scores?.total ?? "", r.scores?.technical ?? "", r.scores?.aptitude ?? "", r.scores?.workstyle ?? "",
      r.correct_answers ?? "", r.total_questions ?? ""];
    if (hasCoding) row.push(r.coding_solved ?? "", r.coding_total ?? "");
    row.push(r.integrity_score ?? "", r.integrity_level || "",
      r.tab_switch_count ?? "", r.window_blur_count ?? "", r.copy_paste_attempts ?? "",
      r.created_at || "");
    return row;
  });

  const csv = [headers, ...rows]
    .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(campaign?.title || "report").replace(/[^a-z0-9]/gi, "_")}_results.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmt(n, fallback = "—") {
  return n == null ? fallback : n;
}

function scoreColor(v) {
  if (v == null) return "";
  if (v >= 75) return "inst-good";
  if (v >= 50) return "inst-warn";
  return "inst-bad";
}

const EVENT_TYPE_LABELS = { INTERVIEW: "AI Interview", MCQ: "MCQ Test", CODING: "Coding Test" };

function EventTypeBadge({ type }) {
  return (
    <span className={`inst-type-badge inst-type-${(type || "").toLowerCase()}`}>
      {EVENT_TYPE_LABELS[type] || type}
    </span>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`inst-pill ${status === "ACTIVE" ? "inst-pill-active" : "inst-pill-inactive"}`}>
      {status}
    </span>
  );
}

// ─── Create Campaign Modal ─────────────────────────────────────────────────────

const EXPERIENCE_OPTIONS = [
  { value: "undergraduate",  label: "Undergraduate" },
  { value: "postgraduate",   label: "Post Graduate" },
];

// Map experience label to numeric string for LLM prompts
const EXP_TO_NUM = { undergraduate: "0", postgraduate: "1" };

const EMPTY_FORM = {
  title: "",
  description: "",
  event_type: "INTERVIEW",
  valid_till: "",
  // INTERVIEW config
  mode: "random_ai",
  uploaded_questions: "",
  // MCQ + shared config
  role: "",
  experience: "undergraduate",
  difficulty: "intermediate",
  category: "generic",
  // MCQ extras
  jd_text: "",
  include_coding: false,
  num_coding_problems: 2,
  // CODING-only campaign config
  num_problems: 2,
  coding_difficulty: "medium",
};

const EMPTY_JD = { file: null, status: "idle", error: "" }; // status: idle|loading|done|error

function CreateCampaignModal({ open, saving, onCreate, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr] = useState("");
  const [jdUpload, setJdUpload] = useState(EMPTY_JD);
  const jdInputRef = React.useRef(null);

  useEffect(() => {
    if (open) { setForm(EMPTY_FORM); setErr(""); setJdUpload(EMPTY_JD); }
  }, [open]);

  const handleJdFile = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "docx"].includes(ext)) {
      setJdUpload({ file: null, status: "error", error: "Only PDF or DOCX files are accepted." });
      return;
    }
    setJdUpload({ file, status: "loading", error: "" });
    try {
      const token = await getAuthToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/institutional/extract-jd`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Extraction failed.");
      setForm((f) => ({ ...f, jd_text: data.text }));
      setJdUpload({ file, status: "done", error: "" });
    } catch (ex) {
      setJdUpload({ file, status: "error", error: ex.message || "Could not read the file." });
    }
  };

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim()) { setErr("Title is required."); return; }
    if (form.event_type === "INTERVIEW" && form.mode !== "random_ai" && !form.uploaded_questions.trim()) {
      setErr("Upload at least one question for this interview mode."); return;
    }
    if (form.event_type === "MCQ" && !form.role.trim()) {
      setErr("Role is required for MCQ tests."); return;
    }
    const expNum = EXP_TO_NUM[form.experience] || "0";
    const config =
      form.event_type === "INTERVIEW"
        ? {
            mode: form.mode,
            uploaded_questions: form.uploaded_questions
              .split("\n")
              .map((q) => q.trim())
              .filter(Boolean),
            role: form.role.trim(),
            experience: expNum,
          }
        : form.event_type === "MCQ"
        ? {
            role: form.role.trim(),
            experience: expNum,
            difficulty: form.difficulty,
            category: form.category,
            jd_text: form.jd_text.trim(),
            include_coding: form.include_coding,
            num_coding_problems: form.include_coding ? Number(form.num_coding_problems) : 0,
          }
        : {
            // CODING
            num_problems: Number(form.num_problems),
            coding_difficulty: form.coding_difficulty,
          };
    try {
      await onCreate({
        title: form.title.trim(),
        description: form.description.trim(),
        event_type: form.event_type,
        valid_till: form.valid_till || null,
        config,
      });
    } catch (ex) {
      setErr(ex.message || "Failed to create campaign.");
    }
  };

  return (
    <div className="inst-modal-backdrop" role="dialog" aria-modal="true">
      <div className="inst-modal">
        <div className="inst-modal-header">
          <h2>Create Assessment Campaign</h2>
          <button className="inst-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <form className="inst-modal-body" onSubmit={handleSubmit}>
          <label className="inst-label">Campaign Title *</label>
          <input
            className="inst-input"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="e.g. Campus Placement 2026 – Round 1"
            maxLength={120}
          />

          <label className="inst-label">Description</label>
          <textarea
            className="inst-input inst-textarea"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Optional brief for candidates"
            rows={2}
          />

          <label className="inst-label">Assessment Type *</label>
          <div className="inst-type-toggle">
            {[
              { value: "INTERVIEW", label: "AI Interview" },
              { value: "MCQ",       label: "MCQ Test" },
              { value: "CODING",    label: "Coding Test" },
            ].map((t) => (
              <button
                key={t.value}
                type="button"
                className={`inst-type-btn${form.event_type === t.value ? " active" : ""}`}
                onClick={() => set("event_type", t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Role — shown for INTERVIEW, MCQ; not CODING */}
          {form.event_type !== "CODING" && (
            <>
              <label className="inst-label">Target Role {form.event_type === "MCQ" ? "*" : ""}</label>
              <input
                className="inst-input"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                placeholder="e.g. Software Engineer, Data Analyst"
              />
            </>
          )}

          {/* JD file upload — MCQ only */}
          {form.event_type === "MCQ" && (
            <>
              <label className="inst-label">
                Job Description
                <span className="inst-label-hint"> — optional, tailors technical questions</span>
              </label>
              <input
                ref={jdInputRef}
                type="file"
                accept=".pdf,.docx"
                style={{ display: "none" }}
                onChange={(e) => handleJdFile(e.target.files?.[0])}
              />
              {jdUpload.status === "idle" || jdUpload.status === "error" ? (
                <button
                  type="button"
                  className="inst-jd-dropzone"
                  onClick={() => jdInputRef.current?.click()}
                >
                  <span className="inst-jd-dropzone__icon">📄</span>
                  <span className="inst-jd-dropzone__text">Click to upload PDF or DOCX</span>
                  <span className="inst-jd-dropzone__hint">Max 10 MB</span>
                  {jdUpload.status === "error" && (
                    <span className="inst-jd-dropzone__err">{jdUpload.error}</span>
                  )}
                </button>
              ) : jdUpload.status === "loading" ? (
                <div className="inst-jd-status inst-jd-status--loading">
                  <span className="inst-jd-spinner" />
                  Extracting text from {jdUpload.file.name}…
                </div>
              ) : (
                <div className="inst-jd-status inst-jd-status--done">
                  <span className="inst-jd-done-icon">✓</span>
                  <span className="inst-jd-filename">{jdUpload.file.name}</span>
                  <span className="inst-jd-chars">{form.jd_text.length} chars extracted</span>
                  <button
                    type="button"
                    className="inst-jd-clear"
                    onClick={() => { setJdUpload(EMPTY_JD); setForm((f) => ({ ...f, jd_text: "" })); }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </>
          )}

          {/* Experience + Difficulty — shown for INTERVIEW and MCQ */}
          {form.event_type !== "CODING" && (
            <div className="inst-row-2">
              <div>
                <label className="inst-label">Experience Level</label>
                <select className="inst-input" value={form.experience} onChange={(e) => set("experience", e.target.value)}>
                  {EXPERIENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="inst-label">Difficulty</label>
                <select className="inst-input" value={form.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </div>
            </div>
          )}

          {/* MCQ coding addon */}
          {form.event_type === "MCQ" && (
            <div className="inst-coding-addon">
              <label className="inst-toggle-switch">
                <input
                  type="checkbox"
                  checked={form.include_coding}
                  onChange={(e) => set("include_coding", e.target.checked)}
                />
                <span className="inst-toggle-rail" />
                <span className="inst-toggle-label">Include Coding Problems after MCQ</span>
              </label>
              {form.include_coding && (
                <div className="inst-coding-addon__picker">
                  <span className="inst-label">Problems</span>
                  <div className="inst-num-toggle">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`inst-type-btn${form.num_coding_problems === n ? " active" : ""}`}
                        onClick={() => set("num_coding_problems", n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* INTERVIEW-specific */}
          {form.event_type === "INTERVIEW" && (
            <>
              <label className="inst-label">Interview Mode</label>
              <select className="inst-input" value={form.mode} onChange={(e) => set("mode", e.target.value)}>
                <option value="random_ai">Fully Random AI Questions</option>
                <option value="around_uploaded">AI Around Uploaded Questions</option>
                <option value="exact_uploaded">Exact Uploaded Questions</option>
              </select>
              {form.mode !== "random_ai" && (
                <>
                  <label className="inst-label">Questions (one per line)</label>
                  <textarea
                    className="inst-input inst-textarea"
                    value={form.uploaded_questions}
                    onChange={(e) => set("uploaded_questions", e.target.value)}
                    placeholder="Describe yourself professionally&#10;What is your greatest technical achievement?&#10;..."
                    rows={5}
                  />
                </>
              )}
            </>
          )}

          {/* CODING-specific */}
          {form.event_type === "CODING" && (
            <>
              <div className="inst-row-2">
                <div>
                  <label className="inst-label">Number of Problems</label>
                  <div className="inst-num-toggle">
                    {[2, 3].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`inst-type-btn${Number(form.num_problems) === n ? " active" : ""}`}
                        onClick={() => set("num_problems", n)}
                      >
                        {n} Problems
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="inst-label">Problem Difficulty</label>
                  <select className="inst-input" value={form.coding_difficulty} onChange={(e) => set("coding_difficulty", e.target.value)}>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="mixed">Mixed (all levels)</option>
                  </select>
                </div>
              </div>
              <p className="inst-coding-hint">
                Problems are picked randomly from the pool for each candidate.
              </p>
            </>
          )}

          <label className="inst-label">Valid Till (optional)</label>
          <input
            className="inst-input"
            type="date"
            value={form.valid_till}
            onChange={(e) => set("valid_till", e.target.value)}
          />

          {err && <p className="inst-form-err">{err}</p>}

          <div className="inst-modal-footer">
            <button type="button" className="inst-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="inst-btn-primary" disabled={saving}>
              {saving ? "Creating…" : "Create Campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Campaign Results Drawer ───────────────────────────────────────────────────

function IntegrityCell({ score, level }) {
  if (!level) return <span className="inst-integrity-lvl">—</span>;
  return (
    <span className={`inst-integrity-lvl ${level}`}>
      {score} <span style={{ opacity: 0.6, fontSize: "0.75em" }}>({level})</span>
    </span>
  );
}

function CampaignResultsDrawer({ campaign, results, loading, error, onClose, onViewReport }) {
  if (!campaign) return null;

  const type = campaign.event_type;
  const hasCoding = !!(campaign.config?.include_coding) || results.some(r => r.coding_solved != null);

  // Sort MCQ by total score desc so top scorers appear first
  const sorted = type === "MCQ"
    ? [...results].sort((a, b) => (b.scores?.total ?? 0) - (a.scores?.total ?? 0))
    : results;

  return (
    <div className="inst-drawer-backdrop" onClick={onClose}>
      <div className="inst-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="inst-drawer-header">
          <div>
            <h3>{campaign.title}</h3>
            <EventTypeBadge type={type} />
            <span className="inst-drawer-count">{results.length} participant{results.length !== 1 ? "s" : ""}</span>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {results.length > 0 && (
              <button
                className="inst-btn-ghost"
                style={{ fontSize: "0.78rem", padding: "0.3rem 0.75rem" }}
                onClick={() => downloadCSV(results, campaign)}
              >
                Download CSV
              </button>
            )}
            <button className="inst-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="inst-drawer-body">
          {loading ? (
            <p className="inst-loading-text">Loading results…</p>
          ) : error ? (
            <div className="inst-error" style={{ margin: "1.5rem 0" }}>
              <p style={{ fontWeight: 600, marginBottom: "0.3rem" }}>Failed to load results</p>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.87rem" }}>{error}</p>
            </div>
          ) : results.length === 0 ? (
            <p className="inst-empty-text">No participants yet.</p>
          ) : type === "INTERVIEW" ? (
            <div className="inst-table-wrap">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Status</th>
                    <th>Overall</th>
                    <th>Technical</th>
                    <th>Communication</th>
                    <th>Integrity</th>
                    <th>Started</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="inst-candidate-name">{r.name || "—"}</div>
                        <div className="inst-candidate-email">{r.email}</div>
                      </td>
                      <td><span className="inst-status-chip">{r.status}</span></td>
                      <td><span className={scoreColor(r.overall_score)}>{fmt(r.overall_score)}</span></td>
                      <td><span className={scoreColor(r.technical_score)}>{fmt(r.technical_score)}</span></td>
                      <td><span className={scoreColor(r.communication_score)}>{fmt(r.communication_score)}</span></td>
                      <td><IntegrityCell score={r.integrity_score} level={r.integrity_level} /></td>
                      <td className="inst-date-cell">{r.started_at || "—"}</td>
                      <td>
                        {r.uid && <button className="inst-report-btn" onClick={() => onViewReport?.(r.uid)}>Report</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : type === "MCQ" ? (
            <div className="inst-table-wrap">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Total %</th>
                    <th>Technical %</th>
                    <th>Aptitude %</th>
                    <th>Workstyle %</th>
                    <th>Correct / Total</th>
                    {hasCoding && <th>Coding</th>}
                    <th>Integrity</th>
                    <th title="Tab switches / Window blurs / Copy-paste">Flags</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="inst-candidate-name">{r.name || "—"}</div>
                        <div className="inst-candidate-email">{r.email || ""}</div>
                      </td>
                      <td><strong className={scoreColor(r.scores?.total)}>{fmt(r.scores?.total)}%</strong></td>
                      <td><span className={scoreColor(r.scores?.technical)}>{fmt(r.scores?.technical)}%</span></td>
                      <td><span className={scoreColor(r.scores?.aptitude)}>{fmt(r.scores?.aptitude)}%</span></td>
                      <td>{fmt(r.scores?.workstyle)}%</td>
                      <td>{r.correct_answers} / {r.total_questions}</td>
                      {hasCoding && (
                        <td>
                          {r.grading_status && r.grading_status !== 'completed'
                            ? <span>{['needs_reconciliation', 'dead_letter'].includes(r.grading_status) ? 'Needs attention' : 'Grading…'}</span>
                            : r.coding_solved != null
                            ? <span className={scoreColor(r.coding_total > 0 ? Math.round((r.coding_solved / r.coding_total) * 100) : 0)}>{r.coding_solved}/{r.coding_total}</span>
                            : <span style={{ color: "rgba(255,255,255,0.3)" }}>—</span>}
                        </td>
                      )}
                      <td><IntegrityCell score={r.integrity_score} level={r.integrity_level} /></td>
                      <td>
                        <span className="inst-flags-cell" title={`Tab: ${r.tab_switch_count ?? 0}  Blur: ${r.window_blur_count ?? 0}  Paste: ${r.copy_paste_attempts ?? 0}`}>
                          {[
                            r.tab_switch_count > 0 && `${r.tab_switch_count}T`,
                            r.window_blur_count > 0 && `${r.window_blur_count}B`,
                            r.copy_paste_attempts > 0 && `${r.copy_paste_attempts}P`,
                          ].filter(Boolean).join(" ") || "—"}
                        </span>
                      </td>
                      <td className="inst-date-cell">{r.created_at || "—"}</td>
                      <td>
                        <button className="inst-report-btn" onClick={() => onViewReport?.(r.uid)}>Report</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* CODING */
            <div className="inst-table-wrap">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Solved</th>
                    <th>Problems</th>
                    <th>Integrity</th>
                    <th title="Tab switches / Window blurs / Copy-paste">Flags</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div className="inst-candidate-name">{r.name || "—"}</div>
                        <div className="inst-candidate-email">{r.email || ""}</div>
                      </td>
                      <td>
                        <strong className={scoreColor(r.total > 0 ? Math.round((r.solved / r.total) * 100) : 0)}>
                          {r.grading_status && r.grading_status !== 'completed'
                            ? (['needs_reconciliation', 'dead_letter'].includes(r.grading_status) ? 'Needs attention' : 'Grading…')
                            : `${r.solved} / ${r.total}`}
                        </strong>
                      </td>
                      <td>
                        {(r.submissions || []).map((s, i) => (
                          <div key={i} className="inst-coding-sub-row">
                            <span className="inst-coding-sub-title">{s.title || `Problem ${i + 1}`}</span>
                            <span className={s.status === "accepted" ? "inst-good" : "inst-bad"}>
                              {s.status || "not run"}
                            </span>
                          </div>
                        ))}
                      </td>
                      <td><IntegrityCell score={r.integrity_score} level={r.integrity_level} /></td>
                      <td>
                        <span className="inst-flags-cell" title={`Tab: ${r.tab_switch_count ?? 0}  Blur: ${r.window_blur_count ?? 0}  Paste: ${r.copy_paste_attempts ?? 0}`}>
                          {[
                            r.tab_switch_count > 0 && `${r.tab_switch_count}T`,
                            r.window_blur_count > 0 && `${r.window_blur_count}B`,
                            r.copy_paste_attempts > 0 && `${r.copy_paste_attempts}P`,
                          ].filter(Boolean).join(" ") || "—"}
                        </span>
                      </td>
                      <td className="inst-date-cell">{r.created_at || "—"}</td>
                      <td>
                        <button className="inst-report-btn" onClick={() => onViewReport?.(r.uid)}>Report</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Practice Monitor Tab ──────────────────────────────────────────────────────

function PracticeMonitor({ monitor }) {
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;
  const { total_students, total_interviews, total_mcq_tests, students, activity_by_day } = monitor;

  const pageRows = students.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(students.length / PAGE_SIZE);

  return (
    <div className="inst-tab-content">
      {/* Stats */}
      <div className="inst-stats-row">
        <div className="inst-stat-card">
          <div className="inst-stat-value">{total_students}</div>
          <div className="inst-stat-label">Enrolled Students</div>
        </div>
        <div className="inst-stat-card">
          <div className="inst-stat-value">{total_interviews}</div>
          <div className="inst-stat-label">Practice Interviews</div>
        </div>
        <div className="inst-stat-card">
          <div className="inst-stat-value">{total_mcq_tests}</div>
          <div className="inst-stat-label">MCQ Tests Taken</div>
        </div>
        <div className="inst-stat-card">
          <div className="inst-stat-value">
            {total_students > 0
              ? Math.round((total_interviews + total_mcq_tests) / total_students)
              : 0}
          </div>
          <div className="inst-stat-label">Avg Activities / Student</div>
        </div>
      </div>

      {/* Activity chart */}
      {activity_by_day.length > 0 && (
        <div className="inst-chart-card">
          <h3>Student Activity (last 30 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={activity_by_day} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.45)" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.45)" }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ background: "#1e2235", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8 }}
                labelStyle={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
              <Bar dataKey="interviews" name="Interviews" fill="#7c6ef2" radius={[3, 3, 0, 0]} />
              <Bar dataKey="mcq" name="MCQ Tests" fill="#36c8a0" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Student table */}
      <div className="inst-section-title">Student Practice Activity</div>
      {students.length === 0 ? (
        <p className="inst-empty-text">
          No students enrolled yet. Share your college enrollment code to get started.
        </p>
      ) : (
        <>
          <div className="inst-table-wrap">
            <table className="inst-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Interviews</th>
                  <th>MCQ Tests</th>
                  <th>Avg Interview Score</th>
                  <th>Avg MCQ Score</th>
                  <th>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((s) => (
                  <tr key={s.uid}>
                    <td>
                      <div className="inst-candidate-name">{s.name || "—"}</div>
                      <div className="inst-candidate-email">{s.email}</div>
                    </td>
                    <td>{s.interview_count}</td>
                    <td>{s.mcq_count}</td>
                    <td>
                      <span className={scoreColor(s.avg_interview_score)}>
                        {s.avg_interview_score != null ? `${s.avg_interview_score}` : "—"}
                      </span>
                    </td>
                    <td>
                      <span className={scoreColor(s.avg_mcq_total)}>
                        {s.avg_mcq_total != null ? `${s.avg_mcq_total}%` : "—"}
                      </span>
                    </td>
                    <td className="inst-date-cell">{s.last_active || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="inst-pagination">
              <button className="inst-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                ← Prev
              </button>
              <span className="inst-page-info">{page + 1} / {totalPages}</span>
              <button className="inst-page-btn" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Recruitment Hub Tab ───────────────────────────────────────────────────────

function RecruitmentHub({ campaigns, onCreateCampaign, onViewResults, onToggleStatus }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copyMsg, setCopyMsg] = useState({});
  const [generatingCampaignId, setGeneratingCampaignId] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(campaigns.length / PAGE_SIZE);
  const pageRows = campaigns.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const result = await onCreateCampaign(payload);
      if (payload.event_type === "MCQ" && result?.campaign_id) {
        // Keep modal open and show generating screen while background thread runs
        setGeneratingCampaignId(result.campaign_id);
      } else {
        setModalOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  // Poll for questions after MCQ campaign creation; close modal once ready
  useEffect(() => {
    if (!generatingCampaignId) return;
    let attempts = 0;
    const id = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API_BASE}/api/institutional/campaigns/${generatingCampaignId}/questions`);
        if (res.ok) {
          const d = await res.json();
          if (d.questions && d.questions.length >= 10) {
            clearInterval(id);
            setGeneratingCampaignId(null);
            setModalOpen(false);
          }
        }
      } catch (_) {}
      if (attempts >= 24) { // give up after 2 min
        clearInterval(id);
        setGeneratingCampaignId(null);
        setModalOpen(false);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [generatingCampaignId]);

  const copyLink = (link, id) => {
    const full = `${window.location.origin}${link}`;
    navigator.clipboard.writeText(full).then(() => {
      setCopyMsg((m) => ({ ...m, [id]: "Copied!" }));
      setTimeout(() => setCopyMsg((m) => ({ ...m, [id]: "" })), 2000);
    });
  };

  const getLinkPath = (c) => {
    if (c.event_type === "INTERVIEW" && c.job_id) return `/interview/${c.job_id}`;
    if (c.event_type === "CODING") return `/inst/coding/${c.id}`;
    return `/inst/mcq/${c.id}`;
  };

  return (
    <div className="inst-tab-content">
      <div className="inst-hub-header">
        <div>
          <p className="inst-hub-desc">
            Create formal assessment campaigns for placements or mock drives. Share the generated link with candidates.
          </p>
        </div>
        <button className="inst-btn-primary" onClick={() => setModalOpen(true)}>
          + New Campaign
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="inst-empty-hub">
          <div className="inst-empty-icon">🎯</div>
          <h3>No campaigns yet</h3>
          <p>Create your first assessment campaign to generate a shareable link for candidates.</p>
          <button className="inst-btn-primary" onClick={() => setModalOpen(true)}>
            Create Campaign
          </button>
        </div>
      ) : (
        <>
        <div className="inst-table-wrap">
          <table className="inst-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Type</th>
                <th>Role</th>
                <th>Difficulty</th>
                <th>Status</th>
                <th>Participants</th>
                <th>Created</th>
                <th>Valid Till</th>
                <th>Link</th>
                <th>Results</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((c) => {
                const linkPath = getLinkPath(c);
                const cfg = c.config || {};
                const roleLabel = cfg.role || "—";
                const diffLabel = cfg.difficulty || cfg.coding_difficulty || "—";
                return (
                  <tr key={c.id}>
                    <td>
                      <div className="inst-campaign-title">{c.title}</div>
                      {c.description && <div className="inst-campaign-desc">{c.description}</div>}
                    </td>
                    <td><EventTypeBadge type={c.event_type} /></td>
                    <td className="inst-role-cell">{roleLabel}</td>
                    <td className="inst-diff-cell">{diffLabel}</td>
                    <td>
                      <StatusPill status={c.status} />
                      <button
                        className={`inst-status-toggle-btn${c.status === "ACTIVE" ? " deactivate" : " activate"}`}
                        onClick={() => onToggleStatus(c)}
                      >
                        {c.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                    <td>{c.participant_count}</td>
                    <td className="inst-date-cell">{c.created_at || "—"}</td>
                    <td className="inst-date-cell">{c.valid_till || "No expiry"}</td>
                    <td>
                      <button
                        className="inst-link-btn"
                        onClick={() => copyLink(linkPath, c.id)}
                        title={`${window.location.origin}${linkPath}`}
                      >
                        {copyMsg[c.id] || "Copy Link"}
                      </button>
                    </td>
                    <td>
                      <button className="inst-results-btn" onClick={() => onViewResults(c)}>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="inst-pagination">
            <button className="inst-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              ← Prev
            </button>
            <span className="inst-page-info">{page + 1} / {totalPages}</span>
            <button className="inst-page-btn" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next →
            </button>
          </div>
        )}
        </>
      )}

      {modalOpen && generatingCampaignId ? (
        <div className="inst-modal-overlay">
          <div className="inst-modal" style={{ textAlign: "center", padding: "2.5rem 2rem" }}>
            <div style={{ fontSize: "2.2rem", marginBottom: "1rem" }}>
              <span style={{ display: "inline-block", animation: "spin 1.2s linear infinite" }}>⚙</span>
            </div>
            <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.6rem" }}>Generating Questions</h2>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.88rem", lineHeight: 1.6 }}>
              Your campaign has been created. We're generating the question set using our AI model — this usually takes 1–2 minutes.
            </p>
            <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem", marginTop: "0.75rem" }}>
              The modal will close automatically once questions are ready.
            </p>
          </div>
        </div>
      ) : (
        <CreateCampaignModal
          open={modalOpen}
          saving={saving}
          onCreate={handleCreate}
          onClose={() => { setModalOpen(false); setGeneratingCampaignId(null); }}
        />
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function InstitutionalDashboard({ user }) {
  const [tab, setTab] = useState("monitor");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignResults, setCampaignResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/institutional/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || d.message || `Error ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleCreateCampaign = async (payload) => {
    const token = await getAuthToken();
    const res = await fetch(`${API_BASE}/api/institutional/campaigns`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || d.message || "Failed to create campaign");
    }
    const d = await res.json();
    await fetchDashboard();
    return d; // { campaign_id, event_type, ... }
  };

  const handleToggleStatus = async (campaign) => {
    const newStatus = campaign.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaign.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || "Failed to update status");
        return;
      }
      setData((prev) => ({
        ...prev,
        campaigns: prev.campaigns.map((c) =>
          c.id === campaign.id ? { ...c, status: newStatus } : c
        ),
      }));
    } catch (e) {
      alert(e.message || "Failed to update status");
    }
  };

  const handleViewResults = async (campaign) => {
    setSelectedCampaign(campaign);
    setResultsLoading(true);
    setResultsError(null);
    setCampaignResults([]);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/institutional/campaigns/${campaign.id}/results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || d.message || `Server error ${res.status}`);
      }
      const d = await res.json();
      setCampaignResults(d.results || []);
    } catch (e) {
      setResultsError(e.message || "Failed to load results.");
      setCampaignResults([]);
    } finally {
      setResultsLoading(false);
    }
  };

  const handleViewReport = async (uid) => {
    if (!selectedCampaign || !uid) return;
    // Open window synchronously (user gesture) then populate async
    const win = window.open("", "_blank", "width=960,height=780");
    win.document.write("<html><body style='font-family:sans-serif;padding:32px;color:#333'><p>Loading report…</p></body></html>");
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE}/api/institutional/campaigns/${selectedCampaign.id}/candidates/${uid}/report`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const report = await res.json();
      win.document.open();
      win.document.write(generateReportHTML(report, selectedCampaign));
      win.document.close();
    } catch (e) {
      win.document.open();
      win.document.write(`<html><body style='font-family:sans-serif;padding:32px;color:#c00'><h2>Error loading report</h2><p>${e.message}</p></body></html>`);
      win.document.close();
    }
  };

  if (loading) {
    return (
      <div className="inst-container">
        <div className="inst-loading">Loading institutional dashboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inst-container">
        <div className="inst-error">
          <h3>Unable to load dashboard</h3>
          <p>{error}</p>
          <button className="inst-btn-primary" onClick={fetchDashboard}>Retry</button>
        </div>
      </div>
    );
  }

  const { college_name, practice_monitor, campaigns } = data;

  return (
    <div className="inst-container">
      {/* Header */}
      <div className="inst-header">
        <div>
          <h1 className="inst-title">{college_name}</h1>
          <p className="inst-subtitle">Institutional Dashboard</p>
        </div>
        <div className="inst-header-stats">
          <div className="inst-hstat">
            <span className="inst-hstat-val">{practice_monitor.total_students}</span>
            <span className="inst-hstat-label">Students</span>
          </div>
          <div className="inst-hstat">
            <span className="inst-hstat-val">{campaigns.length}</span>
            <span className="inst-hstat-label">Campaigns</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="inst-tabs">
        <button
          className={`inst-tab${tab === "monitor" ? " active" : ""}`}
          onClick={() => setTab("monitor")}
        >
          Practice Monitor
        </button>
        <button
          className={`inst-tab${tab === "hub" ? " active" : ""}`}
          onClick={() => setTab("hub")}
        >
          Recruitment Hub
        </button>
      </div>

      {/* Tab content */}
      {tab === "monitor" ? (
        <PracticeMonitor monitor={practice_monitor} />
      ) : (
        <RecruitmentHub
          campaigns={campaigns}
          onCreateCampaign={handleCreateCampaign}
          onViewResults={handleViewResults}
          onToggleStatus={handleToggleStatus}
        />
      )}

      {/* Results drawer */}
      <CampaignResultsDrawer
        campaign={selectedCampaign}
        results={campaignResults}
        loading={resultsLoading}
        error={resultsError}
        onClose={() => { setSelectedCampaign(null); setResultsError(null); }}
        onViewReport={handleViewReport}
      />
    </div>
  );
}
